import { useDataQuery } from '@/hooks/shared/useDataQuery';
import { useHasAuthToken } from '@/hooks/user/useHasAuthToken';
import { useAuthentication } from '@/hooks/user/useAuthentication';
import { QUERY_KEYS } from '@/resources/dataKeys';
import { Api } from '@/utils/api';
import { type SecureFeatureFlagContext } from '@/types/featureFlagContext';

/**
 * Fetch function for obtaining the LaunchDarkly context key.
 * The function signature follows react-query requirements so it can be passed directly to useDataQuery.
 */
export const fetchSecureFeatureFlagContext = async () => {
  const response = await Api.get<SecureFeatureFlagContext>('/api/context_keys');

  return response.data;
};

/**
 * Hook that returns the LaunchDarkly context key used for feature-flag evaluation.
 *
 * Internally it relies on react-query and our `useDataQuery` helper to handle caching,
 * error handling and revalidation.
 *
 * IMPORTANT: Query key includes user UID to prevent stale data when switching users.
 */
export const useSecureFeatureFlagContext = () => {
  const hasAuthToken = useHasAuthToken();
  const { user } = useAuthentication();

  const {
    data: contextKey,
    ...rest
  } = useDataQuery(
    [QUERY_KEYS.secureFeatureFlagContext, user?.uid],
    fetchSecureFeatureFlagContext,
    {
      enabled: hasAuthToken && !!user?.uid,
    },
  );

  return {
    contextKey,
    ...rest,
  };
};

